import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, ChevronLeft, BadgeCheck, Info, Moon, Sun, Monitor, Sparkles, Ban, Loader2 } from "lucide-react";
import { COMPANY } from "@/lib/company";
import { PhoneLink } from "@/components/app/PhoneLink";
import { SETTINGS_SECTIONS } from "@/data/mock";
import { useAuth } from "@/context/AuthContext";
import { AccountSecurity } from "@/components/app/AccountSecurity";
import { WhatsNew } from "@/components/app/WhatsNew";
import { EmailPrefs } from "@/components/app/EmailPrefs";
import { AuraRing } from "@/components/brand/AuraRing";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function Toggle({ on, onClick }) {
  return (
    <button onClick={onClick} className={cn("relative h-6 w-11 rounded-full transition-colors", on ? "aura-gradient" : "bg-am-surface3")}>
      <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all", on ? "left-[22px]" : "left-0.5")} />
    </button>
  );
}

function BlockedAccounts() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => { api.blockedAccounts().then(setList).catch(() => setList([])); }, []);
  const unblock = async (u) => { try { await api.unblock(u.username); setList((l) => l.filter((x) => x.user_id !== u.user_id)); toast(`Unblocked @${u.username}`); } catch (e) { toast(e.message); } };
  if (!list) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>;
  return (
    <div data-testid="blocked-accounts">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Ban className="h-4 w-4 text-aura-magenta" /> Blocked accounts</div>
      {list.length === 0 ? <p className="rounded-xl border border-am-border bg-am-bg/60 p-4 text-sm text-am-text2" data-testid="blocked-empty">You haven't blocked anyone.</p>
        : <div className="divide-y divide-am-border rounded-xl border border-am-border">{list.map((u) => (
          <div key={u.user_id} data-testid={`blocked-${u.username}`} className="flex items-center gap-3 px-3 py-2.5">
            <button onClick={() => navigate(`/@${u.username}`)} className="flex min-w-0 flex-1 items-center gap-2.5 text-left"><AuraRing src={mediaUrl(u.picture) || undefined} size={34} ring={1.5} fallback={u.name} /><div className="min-w-0"><div className="truncate text-sm font-semibold text-white">{u.name}</div><div className="truncate text-xs text-am-text2">@{u.username}</div></div></button>
            <button onClick={() => unblock(u)} data-testid={`unblock-${u.username}`} className="rounded-full border border-am-border-strong px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5">Unblock</button>
          </div>
        ))}</div>}
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [params] = useSearchParams();
  const [active, setActive] = useState(params.get("section") || null);
  const [theme, setTheme] = useState("dark");
  const [toggles, setToggles] = useState({ private: false, aura: true, live: true, mentions: true });
  const [whatsNew, setWhatsNew] = useState(false);
  const t = (k) => setToggles((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="mx-auto max-w-2xl px-4 py-5 md:px-6">
      <h1 className="mb-4 font-display text-2xl font-extrabold text-white">Settings</h1>

      {!active ? (
        <div className="overflow-hidden rounded-2xl border border-am-border bg-am-surface/60">
          {SETTINGS_SECTIONS.map((s, i) => (
            <button key={s.key} onClick={() => setActive(s.key)} data-testid={`settings-${s.key}`} className={cn("flex w-full items-center justify-between px-4 py-4 text-left transition-colors hover:bg-white/5", i > 0 && "border-t border-am-border")}>
              <div>
                <div className="text-sm font-semibold text-white">{s.label}</div>
                <div className="text-xs text-am-text2">{s.desc}</div>
              </div>
              <ChevronRight className="h-5 w-5 text-am-text3" />
            </button>
          ))}
          <button onClick={() => setWhatsNew(true)} data-testid="settings-whats-new" className="flex w-full items-center justify-between border-t border-am-border px-4 py-4 text-left transition-colors hover:bg-white/5">
            <div className="flex items-center gap-3"><Sparkles className="h-5 w-5 aura-text" /><div><div className="text-sm font-semibold text-white">What's new</div><div className="text-xs text-am-text2">Replay the latest update walkthrough</div></div></div>
            <ChevronRight className="h-5 w-5 text-am-text3" />
          </button>
        </div>
      ) : (
        <div>
          <button onClick={() => setActive(null)} className="mb-3 flex items-center gap-1 text-sm text-am-text2 hover:text-white"><ChevronLeft className="h-4 w-4" /> All settings</button>
          <div className="rounded-2xl border border-am-border bg-am-surface/60 p-5">
            <h2 className="mb-4 font-display text-lg font-bold text-white">{SETTINGS_SECTIONS.find((x) => x.key === active)?.label}</h2>

            {(active === "account" || active === "security") && <AccountSecurity section={active} />}

            {active === "verification" && (
              <div>
                <div className="mb-4 flex items-center gap-3 rounded-xl border border-am-border bg-am-bg/60 p-4">
                  <BadgeCheck className="h-6 w-6 text-aura-cyan" />
                  <p className="text-sm text-am-text2">Verification helps establish that a profile represents the person, creator, business or organization it claims to be. A verified identity may eventually become eligible to establish its official Aura Coin.</p>
                </div>
                <button onClick={() => toast("Identity verification is a concept in this prototype.", { description: "Real KYC is not enabled." })} className="w-full rounded-full aura-gradient py-3 text-sm font-bold text-black">Start verification</button>
              </div>
            )}

            {active === "appearance" && (
              <div>
                <p className="mb-3 text-sm text-am-text2">Theme</p>
                <div className="grid grid-cols-3 gap-2">
                  {[{ k: "dark", i: Moon, l: "Dark" }, { k: "light", i: Sun, l: "Light" }, { k: "system", i: Monitor, l: "System" }].map((o) => (
                    <button key={o.k} onClick={() => { setTheme(o.k); if (o.k !== "dark") toast("Dark mode is the AuraMax default", { description: "Other themes are prototype placeholders." }); }} className={cn("flex flex-col items-center gap-2 rounded-xl border px-3 py-4 text-sm font-semibold", theme === o.k ? "border-transparent aura-gradient text-black" : "border-am-border bg-am-surface2 text-am-text2")}>
                      <o.i className="h-5 w-5" /> {o.l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(active === "privacy" || active === "notifications") && (
              <div className="flex flex-col divide-y divide-am-border">
                {(active === "privacy"
                  ? [{ k: "private", l: "Private account", d: "Only followers can see your posts" }]
                  : [{ k: "aura", l: "Aura updates", d: "When your Aura moves" }, { k: "live", l: "Live alerts", d: "When people you follow go live" }, { k: "mentions", l: "Mentions", d: "When someone mentions you" }]
                ).map((row) => (
                  <div key={row.k} className="flex items-center justify-between py-3">
                    <div><div className="text-sm font-semibold text-white">{row.l}</div><div className="text-xs text-am-text2">{row.d}</div></div>
                    <Toggle on={toggles[row.k]} onClick={() => t(row.k)} />
                  </div>
                ))}
                {active === "privacy" && <div className="pt-4"><BlockedAccounts /></div>}
                {active === "notifications" && <div className="mt-3 border-t border-am-border pt-3"><EmailPrefs /></div>}
              </div>
            )}

            {active === "help" && (
              <div className="rounded-xl border border-am-border bg-am-bg/60 p-4" data-testid="settings-help-contact">
                <div className="text-sm font-semibold text-white">Contact AuraMax HQ</div>
                <div className="mt-1"><PhoneLink testid="settings-help-phone" /></div>
                <p className="mt-1 text-xs text-am-text2">{COMPANY.availability}</p>
                <button onClick={() => navigate("/contact")} data-testid="settings-help-contact-link" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-aura-cyan hover:underline">Open contact page <ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
            {active === "about" && (
              <div className="rounded-xl border border-am-border bg-am-bg/60 p-4 text-sm" data-testid="settings-about-company">
                <div className="font-semibold text-white">{COMPANY.brand}</div>
                <div className="text-am-text2">{COMPANY.legalName}</div>
                <a href={COMPANY.website} target="_blank" rel="noopener noreferrer" data-testid="settings-about-website" className="mt-2 inline-block text-aura-cyan hover:underline">{COMPANY.website.replace("https://", "")}</a>
                <div className="mt-2 flex items-center gap-2 text-am-text2">HQ <PhoneLink testid="settings-about-phone" /></div>
                <div className="mt-3 flex gap-4 text-xs"><button onClick={() => navigate("/terms")} data-testid="settings-about-terms" className="text-am-text2 hover:text-white">Terms of Service</button><button onClick={() => navigate("/privacy")} data-testid="settings-about-privacy" className="text-am-text2 hover:text-white">Privacy Policy</button></div>
              </div>
            )}

            {!["verification", "appearance", "privacy", "notifications", "account", "security", "help", "about"].includes(active) && (
              <div className="flex items-center gap-3 rounded-xl border border-am-border bg-am-bg/60 p-4 text-sm text-am-text2">
                <Info className="h-5 w-5 shrink-0 text-am-text3" />
                This section is a prototype placeholder. AuraMax runs in dark mode by default.
              </div>
            )}
          </div>
        </div>
      )}

      <button onClick={async () => { await logout(); navigate("/"); }} data-testid="settings-logout" className="mt-4 w-full rounded-full border border-am-border-strong py-3 text-sm font-semibold text-am-text2 hover:bg-white/5">Log out</button>
      {whatsNew && <WhatsNew forceOpen onClose={() => setWhatsNew(false)} />}
    </div>
  );
}
